import { wishlistItemKey } from '../store/wishlist/wishlistKey';

export interface OpenOrderBasketHintDto {
    appOrderId: number;
    orderType: string;
    salespersonNote?: string;
    orderComment?: string;
    masterid: string;
    colorcode: string;
}

export interface OtherOpenOrderRef {
    appOrderId: number;
    orderType: string;
    /** User-facing order note (SalespersonNote / OrderComment). */
    orderNote?: string;
}

export interface OtherOpenOrderDisplay {
    label: string;
    title?: string;
}

function trimNote(value: string | undefined | null): string {
    if (value === undefined || value === null) {
        return '';
    }
    return String(value).trim();
}

function readHintNote(row: OpenOrderBasketHintDto & { SalespersonNote?: string; OrderComment?: string }): string {
    let fromSales = '';
    if (row.salespersonNote != null) {
        fromSales = String(row.salespersonNote);
    } else if (row.SalespersonNote != null) {
        fromSales = String(row.SalespersonNote);
    }
    let fromComment = '';
    if (row.orderComment != null) {
        fromComment = String(row.orderComment);
    } else if (row.OrderComment != null) {
        fromComment = String(row.OrderComment);
    }
    return trimNote(fromSales) || trimNote(fromComment);
}

function normalizeHintRow(
    row: OpenOrderBasketHintDto & { OrderType?: string; SalespersonNote?: string; OrderComment?: string }
): OpenOrderBasketHintDto | null {
    if (!row || row.appOrderId == null) {
        return null;
    }
    const masterid = row.masterid != null ? String(row.masterid).trim() : '';
    const colorcode = row.colorcode != null ? String(row.colorcode).trim() : '';
    if (!masterid || !colorcode) {
        return null;
    }
    const rawType = row.orderType != null ? row.orderType : row.OrderType;
    const orderType = rawType != null ? String(rawType).trim() : '';
    const orderNote = readHintNote(row);
    return {
        appOrderId: row.appOrderId,
        orderType: orderType,
        salespersonNote: orderNote || undefined,
        masterid: masterid,
        colorcode: colorcode,
    };
}

export function orderNoteForSticker(ref: OtherOpenOrderRef): string {
    const note = trimNote(ref.orderNote);
    if (note) {
        return note;
    }
    const typeLabel = trimNote(ref.orderType) || 'Order';
    return 'On ' + typeLabel;
}

function stickerSummaryForOrder(ref: OtherOpenOrderRef): string {
    return orderNoteForSticker(ref);
}

/** Other open orders per master|color (excludes activeAppOrderId). */
export function buildOtherOpenOrdersByKey(
    hints: OpenOrderBasketHintDto[] | null | undefined,
    activeAppOrderId: number | null | undefined
): Record<string, OtherOpenOrderRef[]> {
    const map: Record<string, OtherOpenOrderRef[]> = {};
    if (!hints || !hints.length) {
        return map;
    }

    const activeId = activeAppOrderId != null ? activeAppOrderId : null;

    for (const raw of hints) {
        const row = normalizeHintRow(raw);
        if (!row) {
            continue;
        }
        if (activeId != null && row.appOrderId === activeId) {
            continue;
        }

        const key = wishlistItemKey(row.masterid, row.colorcode);
        if (!map[key]) {
            map[key] = [];
        }

        const exists = map[key].some((o) => o.appOrderId === row.appOrderId);
        if (!exists) {
            map[key].push({
                appOrderId: row.appOrderId,
                orderType: row.orderType,
                orderNote: row.salespersonNote,
            });
        }
    }

    return map;
}

export function getOtherOpenOrderDisplay(
    otherOrders: OtherOpenOrderRef[] | undefined | null
): OtherOpenOrderDisplay | null {
    if (!otherOrders || !otherOrders.length) {
        return null;
    }

    if (otherOrders.length === 1) {
        const label = stickerSummaryForOrder(otherOrders[0]);
        const fullNote = trimNote(otherOrders[0].orderNote);
        const title =
            fullNote && fullNote !== label
                ? fullNote
                : trimNote(otherOrders[0].orderType) || undefined;
        return { label: label, title: title };
    }

    const count = otherOrders.length;
    const titleParts: string[] = [];
    for (const o of otherOrders) {
        const part = stickerSummaryForOrder(o);
        if (part && titleParts.indexOf(part) === -1) {
            titleParts.push(part);
        }
    }
    return {
        label: 'On ' + count + ' orders',
        title: titleParts.join(' · '),
    };
}

export function masterColorItemsFromStyles(
    products: Array<{ masterId?: string; colorCode?: string }>
): Array<{ master: string; color: string }> {
    const seen: Record<string, boolean> = {};
    const items: Array<{ master: string; color: string }> = [];

    for (const p of products) {
        const master = p.masterId != null ? String(p.masterId).trim() : '';
        const color = p.colorCode != null ? String(p.colorCode).trim() : '';
        if (!master || !color) {
            continue;
        }
        const key = wishlistItemKey(master, color);
        if (seen[key]) {
            continue;
        }
        seen[key] = true;
        items.push({ master: master, color: color });
    }

    return items;
}
